import { addDays, addMonths, startOfMonth, startOfWeek } from 'date-fns';
import { useUpdateUserSettings } from 'features/user';
import { useCallback, useState } from 'react';
import { weekdaysNumberMap } from 'shared/constants';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useToday } from 'shared/contexts/today';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useTodayScrollTime } from 'shared/hooks/use-today-scroll-time';
import { useUser } from 'shared/hooks/use-user';
import { LimitKeys } from 'shared/types/limit-keys';
import { CalendarViewOptions } from 'shared/types/upcoming-view-options';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { targetIsInput } from 'shared/utils/target-is-input';

import { useSelectedCalendarView } from './use-selected-calendar-view';

export const useCalendarViewControls = (withKeys?: boolean) => {
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const [referenceDate, setReferenceDate] = useState(
    startOfWeek(today, { weekStartsOn: weekdaysNumberMap[weekStartsOn] }),
  );

  const updateUserSettings = useUpdateUserSettings();
  const openPremiumDialog = useOpenPremiumDialog();
  const { allowed: isCalendarFourDaysAllowed } = usePremiumFeatureAllowed(
    LimitKeys.DesktopCalendarFourDays,
    {},
  );
  const { allowed: isCalendarWeekAllowed } = usePremiumFeatureAllowed(
    LimitKeys.DesktopCalendarWeek,
    {},
  );
  const { allowed: isCalendarMonthAllowed } = usePremiumFeatureAllowed(
    LimitKeys.DesktopCalendarMonth,
    {},
  );

  const selectedView = useSelectedCalendarView();

  const setSelectedView = useCallback(
    (view: CalendarViewOptions) => {
      updateUserSettings({ upcoming: { webCalendarView: view } });
    },
    [updateUserSettings],
  );

  const onChangeView = useCallback(
    (view: CalendarViewOptions) => {
      if (
        (view === CalendarViewOptions.FourDay && !isCalendarFourDaysAllowed) ||
        (view === CalendarViewOptions.Week && !isCalendarWeekAllowed) ||
        (view === CalendarViewOptions.Month && !isCalendarMonthAllowed)
      ) {
        openPremiumDialog();
        return;
      }

      setSelectedView(view);
      setReferenceDate(
        view === CalendarViewOptions.FourDay
          ? today
          : view === CalendarViewOptions.Week
            ? startOfWeek(today, {
                weekStartsOn: weekdaysNumberMap[weekStartsOn],
              })
            : startOfMonth(today),
      );
    },
    [
      isCalendarFourDaysAllowed,
      isCalendarMonthAllowed,
      isCalendarWeekAllowed,
      openPremiumDialog,
      setSelectedView,
      today,
      weekStartsOn,
    ],
  );

  const onPrev = useCallback(
    () =>
      (isCalendarWeekAllowed || isCalendarMonthAllowed) &&
      setReferenceDate((oldDate) =>
        selectedView === CalendarViewOptions.FourDay
          ? addDays(oldDate, -1)
          : selectedView === CalendarViewOptions.Week
            ? addDays(oldDate, -1)
            : addMonths(oldDate, -1),
      ),
    [isCalendarMonthAllowed, isCalendarWeekAllowed, selectedView],
  );

  const onBigPrev = useCallback(
    () =>
      (isCalendarWeekAllowed || isCalendarMonthAllowed) &&
      setReferenceDate((oldDate) =>
        selectedView === CalendarViewOptions.FourDay
          ? addDays(oldDate, -4)
          : selectedView === CalendarViewOptions.Week
            ? addDays(oldDate, -7)
            : addMonths(oldDate, -1),
      ),
    [isCalendarMonthAllowed, isCalendarWeekAllowed, selectedView],
  );

  const onToday = useCallback(
    () =>
      (isCalendarWeekAllowed || isCalendarMonthAllowed) &&
      setReferenceDate(
        selectedView === CalendarViewOptions.FourDay
          ? today
          : selectedView === CalendarViewOptions.Week
            ? startOfWeek(today, {
                weekStartsOn: weekdaysNumberMap[weekStartsOn],
              })
            : startOfMonth(today),
      ),
    [
      isCalendarMonthAllowed,
      isCalendarWeekAllowed,
      selectedView,
      today,
      weekStartsOn,
    ],
  );

  const onNext = useCallback(
    () =>
      (isCalendarWeekAllowed || isCalendarMonthAllowed) &&
      setReferenceDate((oldDate) =>
        selectedView === CalendarViewOptions.FourDay
          ? addDays(oldDate, 1)
          : selectedView === CalendarViewOptions.Week
            ? addDays(oldDate, 1)
            : addMonths(oldDate, 1),
      ),
    [isCalendarMonthAllowed, isCalendarWeekAllowed, selectedView],
  );

  const onBigNext = useCallback(
    () =>
      (isCalendarWeekAllowed || isCalendarMonthAllowed) &&
      setReferenceDate((oldDate) =>
        selectedView === CalendarViewOptions.FourDay
          ? addDays(oldDate, 4)
          : selectedView === CalendarViewOptions.Week
            ? addDays(oldDate, 7)
            : addMonths(oldDate, 1),
      ),
    [isCalendarMonthAllowed, isCalendarWeekAllowed, selectedView],
  );

  const { scrollTime } = useTodayScrollTime(withKeys, onToday);

  // Keyboard shortcuts
  useKeysDown(
    ['KeyM'],
    (e) => {
      if (!withKeys || targetIsInput(e.target)) {
        return; // Don't do a thing
      }

      if (!isCalendarMonthAllowed) {
        openPremiumDialog();
        return;
      }

      setSelectedView(CalendarViewOptions.Month);
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['KeyW'],
    (e) => {
      if (!withKeys || targetIsInput(e.target)) {
        return; // Don't do a thing
      }

      if (!isCalendarWeekAllowed) {
        openPremiumDialog();
        return;
      }

      setSelectedView(CalendarViewOptions.Week);
    },
    { otherKeysInCombinationAllowed: true },
  );

  useKeysDown(
    ['KeyD'],
    (e) => {
      if (!withKeys || targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      if (!isCalendarWeekAllowed) {
        openPremiumDialog();
        return;
      }
      setSelectedView(CalendarViewOptions.FourDay);
    },
    { otherKeysInCombinationAllowed: true },
  );

  useKeysDown(
    [
      ['ShiftLeft', 'ArrowLeft'],
      ['ShiftRight', 'ArrowLeft'],
    ],
    (e) => {
      if (!withKeys || targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onBigPrev();
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['ArrowLeft'],
    (e) => {
      if (!withKeys || targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onPrev();
    },
    { otherKeysInCombinationAllowed: true },
  );

  useKeysDown(
    [
      ['ShiftLeft', 'ArrowRight'],
      ['ShiftRight', 'ArrowRight'],
    ],
    (e) => {
      if (!withKeys || targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onBigNext();
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['ArrowRight'],
    (e) => {
      if (!withKeys || targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onNext();
    },
    { otherKeysInCombinationAllowed: true },
  );

  return {
    referenceDate,
    selectedView,
    onChangeView,
    onPrev,
    onBigPrev,
    onToday,
    onNext,
    onBigNext,
    scrollTime,
  };
};
